import { Box, type SxProps, type Theme } from "@mui/material";
import { PageHeaderWrapper } from "@src/appV2/redesign/system/ui-components";
import { type ReactNode, useCallback, useEffect, useRef } from "react";

import { PageHeaderTopRow, type PageHeaderTopRowProps } from "./PageHeaderTopRow";

interface CollapsiblePageHeaderProps extends Pick<
  PageHeaderTopRowProps,
  "fallbackGoBackPath" | "rightAction"
> {
  collapsedTitle: ReactNode;
  children: ReactNode;
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  headerSx?: SxProps<Theme>;
  topRowSx?: SxProps<Theme>;
}

function applyCollapseTransition(
  wrapperElement: HTMLElement,
  titleElement: HTMLElement,
  progress: number,
  contentHeight: number
) {
  wrapperElement.style.height = `${contentHeight * (1 - progress)}px`;
  wrapperElement.style.opacity = `${1 - progress}`;
  titleElement.style.opacity = `${progress}`;
}

export function CollapsiblePageHeader(props: CollapsiblePageHeaderProps) {
  const {
    collapsedTitle,
    children,
    scrollContainerRef,
    headerSx,
    topRowSx,
    fallbackGoBackPath,
    rightAction,
  } = props;

  const expandedContentRef = useRef<HTMLDivElement>(null);
  const expandedWrapperRef = useRef<HTMLDivElement>(null);
  const collapsedTitleRef = useRef<HTMLSpanElement>(null);
  const expandedContentHeight = useRef(0);

  const applyCollapseProgress = useCallback(
    (scrollTop: number) => {
      const contentHeight = expandedContentHeight.current;
      if (contentHeight === 0) {
        return;
      }

      const progress = Math.min(scrollTop / contentHeight, 1);

      const wrapperElement = expandedWrapperRef.current;
      const titleElement = collapsedTitleRef.current;
      if (wrapperElement && titleElement) {
        applyCollapseTransition(wrapperElement, titleElement, progress, contentHeight);
      }

      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        const collapseAmount = contentHeight * progress;
        scrollContainer.style.paddingTop = collapseAmount > 0 ? `${collapseAmount}px` : "";
      }
    },
    [scrollContainerRef]
  );

  useEffect(() => {
    if (expandedContentRef.current) {
      expandedContentHeight.current = expandedContentRef.current.getBoundingClientRect().height;
    }
  }, []);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return;
    }

    const handleScroll = () => {
      applyCollapseProgress(scrollContainer.scrollTop);
    };

    scrollContainer.addEventListener("scroll", handleScroll, {
      passive: true,
    });
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      scrollContainer.style.paddingTop = "";
    };
  }, [scrollContainerRef, applyCollapseProgress]);

  return (
    <PageHeaderWrapper sx={headerSx}>
      <PageHeaderTopRow
        fallbackGoBackPath={fallbackGoBackPath}
        rightAction={rightAction}
        title={
          <Box
            ref={collapsedTitleRef}
            component="span"
            sx={{ opacity: 0, transition: "opacity 100ms ease-out" }}
          >
            {collapsedTitle}
          </Box>
        }
        sx={topRowSx}
      />

      <Box
        ref={expandedWrapperRef}
        sx={{ overflow: "hidden", transition: "height 100ms ease-out, opacity 100ms ease-out" }}
      >
        <Box ref={expandedContentRef}>{children}</Box>
      </Box>
    </PageHeaderWrapper>
  );
}
